import React, { useEffect, useState } from 'react'
import { apiURL } from '../utilities/URLs'
import { Chart, CategoryScale, LinearScale, BarElement, Tooltip, LineElement, PointElement, LineController } from 'chart.js'
import { Bar, Line } from 'react-chartjs-2'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.module.css'
import { affiliateData, company, StatsData } from '../data/AffiliateData'
import { useTheme } from '../ThemeContext'
import { refreshAccessToken } from '../utilities/extensions'
import { useNavigate } from 'react-router-dom'

Chart.register(CategoryScale, LinearScale, LineElement, PointElement, LineController, BarElement, Tooltip)

function getColorClass(percentage) {
  if (percentage >= 70) return 'text-green-600'
  if (percentage >= 40) return 'text-yellow-400'
  return 'text-red-600'
}

export default function Stats() {
  const navigate = useNavigate()
  const { isDarkMode } = useTheme()

  const [fetchingData, setFetchingData] = useState(true)
  const [selectedTab, setSelectedTab] = useState('allTime')
  const [statsData, setStatsData] = useState(new StatsData())

  const [selectedDays, setSelectedDays] = useState('')
  const [dateOptions, setDateOptions] = useState([])
  const [dailyRecords, setDailyRecords] = useState([])

  const [selectedDate, setSelectedDate] = useState(new Date())
  const [daily15MinuteRecords, setDaily15MinuteRecords] = useState([])

  const chartData = {
    labels: dailyRecords.map(entry => entry.date.slice(0, 10)),
    datasets: [
      {
        label: 'Number of Leads',
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1,
        hoverBackgroundColor: 'rgba(75, 192, 192, 0.8)',
        hoverBorderColor: 'rgba(75, 192, 192, 1)',
        data: dailyRecords.map(entry => entry.numOfRecords),
        barThickness: 'flex',
      },
    ],
  }

  const lineData = {
    labels: daily15MinuteRecords.map(entry => {
      const date = new Date(entry.date)
      const minutes = date.getMinutes()
      return minutes === 0 ? date.toLocaleTimeString() : ''
    }),
    datasets: [
      {
        label: 'Number of Leads',
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1,
        hoverBackgroundColor: 'rgba(75, 192, 192, 0.8)',
        hoverBorderColor: 'rgba(75, 192, 192, 1)',
        data: daily15MinuteRecords.map(entry => entry.numOfRecords),
        fill: false,
        tension: 0.1,
      }
    ]
  }

  const options = {
    plugins: {
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            return `Total Leads: ${tooltipItem.raw}`
          }
        }
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'x',
    scales: {
      x: {
        ticks: {
          color: isDarkMode ? '#ffffff' : '#000000',
        },
        grid: {
          color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
          borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
        },
      },
      y: {
        ticks: {
          color: isDarkMode ? '#ffffff' : '#000000',
        },
        grid: {
          color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
          borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
        },
      },
    },
  }

  const generateDateOptions = async () => {
    let currentDate = new Date()
    let options = []
    let startDate = new Date(currentDate)

    while (startDate >= new Date(2024, 0, 1)) {
      let endDate = new Date(startDate)
      startDate.setDate(startDate.getDate() - 13)
      let optionValue = `${startDate.toISOString().slice(0, 10)} - ${endDate.toISOString().slice(0, 10)}`
      let optionLabel = `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
      options.push({ value: optionValue, label: optionLabel })
      startDate.setDate(startDate.getDate() - 1)
    }

    setSelectedDays(options[0]?.value || '')
    setDateOptions(options)

    await fetchDailyRecords(options[0]?.value)
  }

  const fetchDailyRecords = async (selectedRange) => {
    setSelectedDays(selectedRange)
    const [startDateStr, endDateStr] = selectedRange.split(' - ')
    const dailyRecordsResponse = await fetch(`${apiURL}/api/getDailyRecords?affiliateId=${affiliateData.account.accountId}&startDate=${encodeURIComponent(startDateStr)}&endDate=${encodeURIComponent(endDateStr)}`, {
      headers: {
        'Authorization': affiliateData.accessToken
      }
    })
    const dailyRecordsData = await dailyRecordsResponse.json()
    if (dailyRecordsData.response.error) {
      if (dailyRecordsData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await fetchDailyRecords(selectedRange) : navigate('/affiliate', { state: { expired: true } })
      } else
        console.error(dailyRecordsData.response.error)
    } else
      setDailyRecords(dailyRecordsData.response.dailyRecords)
  }

  const fetchDaily15MinRecords = async (_selectedDate) => {
    setSelectedDate(_selectedDate)
    const estDate = new Date(_selectedDate.toLocaleString('en-US', { timezone: 'America/New_York' }))

    const daily15MinRecordsResponse = await fetch(`${apiURL}/api/getDaily15MinRecords?affiliateId=${affiliateData.account.accountId}&date=${encodeURIComponent(estDate.toISOString())}`, {
      headers: {
        'Authorization': affiliateData.accessToken
      }
    })
    const daily15MinRecordsData = await daily15MinRecordsResponse.json()
    if (daily15MinRecordsData.response.error) {
      if (daily15MinRecordsData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await fetchDaily15MinRecords(_selectedDate) : navigate('/affiliate', { state: { expired: true } })
      } else
        console.error(daily15MinRecordsData.response.error)
    } else
      setDaily15MinuteRecords(daily15MinRecordsData.response.daily15MinRecords)
  }

  const fetchAllTimeData = async () => {
    const allTimeStatsResponse = await fetch(`${apiURL}/api/getStats?affiliateId=${affiliateData.account.accountId}&isAllTime=1`, {
      headers: {
        'Authorization': affiliateData.accessToken
      }
    })
    const allTimeStatsData = await allTimeStatsResponse.json()
    if (allTimeStatsData.response.error) {
      if (allTimeStatsData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await fetchAllTimeData() : navigate('/affiliate', { state: { expired: true } })
      } else
        console.error(allTimeStatsData.response.error)
    } else {
      setStatsData(new StatsData(allTimeStatsData.response.stats))
      await fetchDailyRecords(selectedDays)
    }
  }

  const fetchDailyData = async (_selectedDate) => {
    const estDate = new Date(_selectedDate.toLocaleString('en-US', { timezone: 'America/New_York' }))

    const dailyStatsResponse = await fetch(`${apiURL}/api/getStats?affiliateId=${affiliateData.account.accountId}&isAllTime=0&date=${encodeURIComponent(estDate.toISOString())}`, {
      headers: {
        'Authorization': affiliateData.accessToken
      }
    })
    const dailyStatsData = await dailyStatsResponse.json()
    if (dailyStatsData.response.error) {
      if (dailyStatsData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await fetchDailyData(_selectedDate) : navigate('/affiliate', { state: { expired: true } })
      } else
        console.error(dailyStatsData.response.error)
    } else {
      setStatsData(new StatsData(dailyStatsData.response.stats))
      await fetchDaily15MinRecords(_selectedDate)
    }
  }

  const handleSwitchTab = async (selectedDate = new Date()) => {
    setFetchingData(true)

    if (selectedTab === 'allTime') {
      setSelectedTab('daily')
      await fetchDailyData(selectedDate)
    } else {
      setSelectedTab('allTime')
      await fetchAllTimeData()
    }

    setFetchingData(false)
  }

  useEffect(() => {
    const handleGetStats = async () => {
      const statsResponse = await fetch(`${apiURL}/api/getStats?affiliateId=${affiliateData.account.accountId}&isAllTime=1`, {
        headers: {
          'Authorization': affiliateData.accessToken
        }
      })
      const statsData = await statsResponse.json()
      if (statsData.response.error) {
        if (statsData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleGetStats() : navigate('/affiliate', { state: { expired: true } })
        } else
          console.error(statsData.response.error)
      } else {
        setStatsData(new StatsData(statsData.response.stats))
        await generateDateOptions()
        setFetchingData(false)
      }
    }

    document.title = `${company.companyName} | Statistics`
    handleGetStats()
  }, [])

  return (
    <div className={`w-full h-full flex flex-col items-center font-mont p-10 ${isDarkMode && 'bg-[#1a1d21]'}`}>
      <h2 className={`font-bold text-4xl mb-4 text-gray-800 ${isDarkMode && 'text-white'}`}>Statistics</h2>
      <div className='flex flex-row items-center gap-5 mb-5'>
        <button
          className={`btn-underline ${selectedTab === 'allTime' ? 'active text-blue-500' : `${isDarkMode && 'text-white'}`} hover:text-blue-500`}
          onClick={() => handleSwitchTab()}>
          All Time
        </button>
        <button
          className={`btn-underline ${selectedTab === 'daily' ? 'active text-setcg' : `${isDarkMode && 'text-white'}`} hover:text-setcg`}
          onClick={() => handleSwitchTab()}>
          Daily
        </button>
      </div>
      {fetchingData ? (
        <div className='flex flex-row items-center justify-center gap-2 -mb-5'>
          <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
          <p className={`${isDarkMode && 'text-white'}`}>Fetching Statistics...</p>
        </div>
      ) : (
        <>
          {selectedTab === 'daily' && (
            <div className='w-full flex justify-end mr-[35vw] mb-5'>
              <div className='flex items-center max-sm:flex-col max-sm:items-end'>
                <label className={`mr-2 ${isDarkMode && 'text-white'}`}>Select Date:</label>
                <DatePicker
                  selected={selectedDate}
                  onChange={fetchDailyData}
                  className={`border rounded-lg px-4 py-2 transition-colors duration-200 ease-in-out shadow-sm focus:outline-none focus:ring-2 ${isDarkMode ? 'bg-gray-700 text-white border-gray-600 focus:ring-gray-500' : 'bg-white text-gray-900 border-gray-300 focus:ring-blue-500'}`}
                  dateFormat='yyyy-MM-dd'
                  placeholderText='Click to select a date'
                  maxDate={new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York' })}
                />
              </div>
            </div>
          )}
          <div className='flex flex-col gap-8 max-w-6xl w-full'>
            <div className='w-full'>
              <h3 className={`font-semibold text-lg text-gray-700 ${isDarkMode && 'text-white'}`}>SETC Conversion Data</h3>
              <div className={`tile shadow-lg rounded-lg text-center p-8 relative min-h-40 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                  <div className='lg:border-r max-sm:border-b border-gray-300 lg:pr-4'>
                    <h3 className={`font-semibold text-lg mb-4 text-gray-700 ${isDarkMode && 'text-white'}`}>Tax Status Conversion</h3>
                    <div className={`flex items-center justify-center ${getColorClass(statsData.taxStatusCompletePercentage.percentage)}`}>
                      <p className='text-4xl font-bold'>{statsData.taxStatusCompletePercentage.percentage}%</p>
                    </div>
                    <div className={`mt-4 w-full text-end lg:pr-4 text-sm max-sm:mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Total Leads: {statsData.taxStatusCompletePercentage.totalLeads.toLocaleString()}
                    </div>
                  </div>

                  <div className='lg:border-r max-sm:border-b border-gray-300 lg:pr-4'>
                    <h3 className={`font-semibold text-lg mb-4 text-gray-700 ${isDarkMode && 'text-white'}`}>Customer Portal Conversion</h3>
                    <div className={`flex items-center justify-center ${getColorClass(statsData.customerPortalPercentage.percentage)}`}>
                      <p className='text-4xl font-bold'>{statsData.customerPortalPercentage.percentage}%</p>
                    </div>
                    <div className={`mt-4 w-full text-end lg:pr-4 text-sm max-sm:mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Total Leads: {statsData.customerPortalPercentage.totalLeads.toLocaleString()}
                    </div>
                  </div>

                  <div>
                    <h3 className={`font-semibold text-lg mb-4 text-gray-700 ${isDarkMode && 'text-white'}`}>Banking Info Added Conversion</h3>
                    <div className={`flex items-center justify-center ${getColorClass(statsData.bankingInfoAddedPercentage.percentage)}`}>
                      <p className='text-4xl font-bold'>{statsData.bankingInfoAddedPercentage.percentage}%</p>
                    </div>
                    <div className={`mt-4 w-full text-end lg:pr-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Total Leads: {statsData.bankingInfoAddedPercentage.totalLeads.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='w-full'>
              <h3 className={`font-semibold text-lg text-gray-700 ${isDarkMode && 'text-white'}`}>SETC Calculator Data</h3>
              <div className={`tile shadow-lg rounded-lg text-center p-8 relative min-h-40 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                  {selectedTab === 'daily' && (
                    <div className='lg:border-r max-sm:border-b border-gray-300 lg:pr-4'>
                      <h3 className={`font-semibold text-lg mb-4 text-gray-700 ${isDarkMode && 'text-white'}`}>Total Calculations</h3>
                      <div className={`flex items-center justify-center ${getColorClass(100)}`}>
                        <p className='text-4xl font-bold max-sm:mb-6'>{statsData.calculatorData.calculatorData.totalLeads.toLocaleString()}</p>
                      </div>
                    </div>
                  )}

                  <div className='lg:border-r max-sm:border-b border-gray-300 lg:pr-4'>
                    <h3 className={`font-semibold text-lg mb-4 text-gray-700 ${isDarkMode && 'text-white'}`}>DNQ Percentage</h3>
                    <div className={`flex items-center justify-center ${getColorClass(statsData.calculatorData.dnqData.percentage)}`}>
                      <p className='text-4xl font-bold'>{statsData.calculatorData.dnqData.percentage}%</p>
                    </div>
                    <div className={`mt-4 w-full text-end lg:pr-4 text-sm max-sm:mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Total Leads: {statsData.calculatorData.dnqData.totalLeads.toLocaleString()}
                    </div>
                  </div>

                  <div className={`${selectedTab === 'allTime' && 'lg:border-r'} max-sm:border-b border-gray-300`}>
                    <h3 className={`font-semibold text-lg mb-4 text-gray-700 ${isDarkMode && 'text-white'}`}>Refund Percentage</h3>
                    <div className={`flex items-center justify-center ${getColorClass(100)}`}>
                      <p className='text-4xl font-bold'>{statsData.calculatorData.refundData.percentage}%</p>
                    </div>
                    <div className={`mt-4 w-full text-end lg:pr-4 text-sm max-sm:mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Total Leads: {statsData.calculatorData.refundData.totalLeads.toLocaleString()}
                    </div>
                  </div>

                  <div>
                    <h3 className={`font-semibold text-lg mb-4 text-gray-700 ${selectedTab === 'daily' && 'mt-10'} ${isDarkMode && 'text-white'}`}>Average Refund Amount</h3>
                    <div className={`flex items-center justify-center ${getColorClass(100)}`}>
                      <p className='text-4xl font-bold'>${statsData.avgRefundAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>


          <div className='mt-10 max-w-6xl w-full'>
            {selectedTab === 'allTime' ? (
              <>
                <div className='flex max-sm:flex-col justify-between w-full max-w-6xl'>
                  <h3 className={`font-semibold text-lg mb-4 ${isDarkMode ? 'text-white' : 'text-gray-700'}`}>Daily Records</h3>
                  <div className='flex items-center max-sm:flex-col max-sm:items-end'>
                    <label className={`mr-2 ${isDarkMode && 'text-white'}`}>Select Date Range:</label>
                    <select
                      className={`border rounded-lg px-4 py-2 transition-colors duration-200 ease-in-out shadow-sm focus:outline-none focus:ring-2 ${isDarkMode ? 'bg-gray-700 text-white border-gray-600 focus:ring-gray-500' : 'bg-white text-gray-900 border-gray-300 focus:ring-blue-500'}`}
                      value={selectedDays}
                      onChange={(e) => fetchDailyRecords(e.target.value)}
                    >
                      {dateOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className='h-96 md:h-80'>
                  <Bar data={chartData} options={options} />
                </div>
              </>
            ) : (
              <>
                <div className='flex max-sm:flex-col justify-between w-full max-w-6xl mt-10'>
                  <h3 className={`font-semibold text-lg mb-4 text-gray-700 ${isDarkMode && 'text-white'}`}>Daily Records (15 Min Increments)</h3>
                </div>
                <div className='h-96 md:h-80'>
                  <Line data={lineData} options={options} />
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}